import { MutableRefObject, RefObject, useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { applyWaveSurferLayout, createWaveSurferOptions } from './wave-surfer-layout';

interface IUseWaveSurferOptions {
    src?: string;
    blob?: Blob | null;
    maxDurationSeconds?: number;
    beforeDestroy?: () => void;
}

interface IWaveSurferPlayer {
    waveRef: RefObject<HTMLDivElement>;
    wavesurferRef: MutableRefObject<WaveSurfer | null>;
    mountedRef: MutableRefObject<boolean>;
    isPlaying: boolean;
    setIsPlaying: (playing: boolean) => void;
    playbackSeconds: number;
    setPlaybackSeconds: (seconds: number) => void;
    durationSeconds: number;
    setDurationSeconds: (seconds: number) => void;
    hasWave: boolean;
    setHasWave: (hasWave: boolean) => void;
    togglePlay: () => void;
    recreate: () => WaveSurfer | null;
}

const useWaveSurfer = ({
    src,
    blob,
    maxDurationSeconds,
    beforeDestroy,
}: IUseWaveSurferOptions = {}): IWaveSurferPlayer => {
    const waveRef = useRef<HTMLDivElement | null>(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);
    const beforeDestroyRef = useRef(beforeDestroy);
    const mountedRef = useRef(true);
    beforeDestroyRef.current = beforeDestroy;
    const fallbackAudioRef = useRef<HTMLAudioElement | null>(null);
    const maxDurationRef = useRef(maxDurationSeconds);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackSeconds, setPlaybackSeconds] = useState(0);
    const [durationSeconds, setDurationSeconds] = useState(0);
    const [hasWave, setHasWave] = useState(false);

    maxDurationRef.current = maxDurationSeconds;

    const releaseFallback = (): void => {
        const audio = fallbackAudioRef.current;
        if (!audio) {
            return;
        }
        audio.pause();
        audio.src = '';
        fallbackAudioRef.current = null;
    };

    const openWaveSurfer = (): WaveSurfer | null => {
        const container = waveRef.current;
        if (!container) {
            return null;
        }
        const ws = WaveSurfer.create({
            ...createWaveSurferOptions(),
            container: container,
        });
        applyWaveSurferLayout(container);
        wavesurferRef.current = ws;

        ws.on('play', () => {
            if (mountedRef.current) {
                setIsPlaying(true);
            }
        });
        ws.on('pause', () => {
            if (mountedRef.current) {
                setIsPlaying(false);
            }
        });
        ws.on('finish', () => {
            if (!mountedRef.current) {
                return;
            }
            setIsPlaying(false);
            setPlaybackSeconds(0);
        });
        ws.on('timeupdate', (time: number) => {
            if (!mountedRef.current) {
                return;
            }
            const next = Math.floor(time);
            setPlaybackSeconds(previous => previous === next ? previous : next);
        });
        ws.on('ready', (duration: number) => {
            if (!mountedRef.current) {
                return;
            }
            const rounded = Math.max(0, Math.round(duration));
            const cap = maxDurationRef.current;
            const shown = cap === undefined ? rounded : Math.min(cap, rounded);
            setDurationSeconds(previous => previous === shown ? previous : shown);
            setHasWave(true);
        });
        ws.on('click', () => {
            if (!mountedRef.current || ws.isPlaying() || ws.getDuration() <= 0) {
                return;
            }
            ws.play()
                .then(() => {
                    if (mountedRef.current) {
                        setIsPlaying(true);
                    }
                })
                .catch(() => {
                    if (mountedRef.current) {
                        setIsPlaying(false);
                    }
                });
        });
        ws.on('error', () => undefined);
        return ws;
    };

    const destroyWaveSurfer = (): void => {
        beforeDestroyRef.current?.();
        const current = wavesurferRef.current;
        wavesurferRef.current = null;
        current?.destroy();
    };

    const recreate = (): WaveSurfer | null => {
        destroyWaveSurfer();
        releaseFallback();
        setIsPlaying(false);
        setPlaybackSeconds(0);
        setDurationSeconds(0);
        setHasWave(false);
        return openWaveSurfer();
    };

    useEffect(() => {
        mountedRef.current = true;
        openWaveSurfer();
        return () => {
            mountedRef.current = false;
            releaseFallback();
            destroyWaveSurfer();
        };
    }, []);

    useEffect(() => {
        const remoteSrc = src && !src.startsWith('blob:') ? src : undefined;
        const ws = wavesurferRef.current;
        if (remoteSrc === undefined && !blob) {
            if (src === undefined) {
                return undefined;
            }
            if (!ws) {
                return undefined;
            }
            setIsPlaying(false);
            setPlaybackSeconds(0);
            releaseFallback();
            ws.toggleInteraction(false);
            ws.empty();
            setDurationSeconds(0);
            setHasWave(false);
            return undefined;
        }
        if (!ws) {
            return undefined;
        }
        setIsPlaying(false);
        setPlaybackSeconds(0);
        releaseFallback();
        ws.toggleInteraction(true);
        let cancelled = false;
        const loadAudio = blob ? ws.loadBlob(blob) : ws.load(remoteSrc);
        loadAudio.catch(() => {
            if (cancelled || !mountedRef.current) {
                return;
            }
            setHasWave(false);
            ws.toggleInteraction(false);
            const fallbackSrc = blob ? URL.createObjectURL(blob) : remoteSrc;
            if (!fallbackSrc) {
                return;
            }
            const audio = new Audio(fallbackSrc);
            fallbackAudioRef.current = audio;
            audio.addEventListener('loadedmetadata', () => {
                if (!mountedRef.current || !Number.isFinite(audio.duration)) {
                    return;
                }
                const shown = Math.max(0, Math.round(audio.duration));
                setDurationSeconds(previous => previous === shown ? previous : shown);
            });
            audio.addEventListener('timeupdate', () => {
                if (!mountedRef.current) {
                    return;
                }
                const next = Math.floor(audio.currentTime);
                setPlaybackSeconds(previous => previous === next ? previous : next);
            });
            audio.addEventListener('ended', () => {
                if (!mountedRef.current) {
                    return;
                }
                setIsPlaying(false);
                setPlaybackSeconds(0);
            });
        });
        return () => {
            cancelled = true;
            releaseFallback();
        };
    }, [src, blob]);

    const togglePlay = (): void => {
        const fallback = fallbackAudioRef.current;
        if (fallback) {
            if (!fallback.paused) {
                fallback.pause();
                setIsPlaying(false);
                return;
            }
            if (fallback.ended) {
                fallback.currentTime = 0;
            }
            fallback.play()
                .then(() => {
                    if (mountedRef.current) {
                        setIsPlaying(true);
                    }
                })
                .catch(() => {
                    if (mountedRef.current) {
                        setIsPlaying(false);
                    }
                });
            return;
        }
        const ws = wavesurferRef.current;
        if (!ws || (!blob && src === '')) {
            return;
        }
        if (ws.isPlaying()) {
            ws.pause();
            setIsPlaying(false);
            return;
        }
        const duration = ws.getDuration();
        if (duration > 0 && ws.getCurrentTime() >= duration - 0.05) {
            ws.setTime(0);
        }
        ws.play()
            .then(() => {
                if (mountedRef.current) {
                    setIsPlaying(true);
                }
            })
            .catch(() => {
                if (mountedRef.current) {
                    setIsPlaying(false);
                }
            });
    };

    return {
        waveRef: waveRef,
        wavesurferRef: wavesurferRef,
        mountedRef: mountedRef,
        isPlaying: isPlaying,
        setIsPlaying: setIsPlaying,
        playbackSeconds: playbackSeconds,
        setPlaybackSeconds: setPlaybackSeconds,
        durationSeconds: durationSeconds,
        setDurationSeconds: setDurationSeconds,
        hasWave: hasWave,
        setHasWave: setHasWave,
        togglePlay: togglePlay,
        recreate: recreate,
    };
};

export { useWaveSurfer };
